import { useSearchParams } from 'next/navigation';
import useSWR from 'swr';

import { Pack, Packs } from '@/components/app/cards/PacksList/types';
import { Tag, Tags } from '@/components/app/cards/TagsList/types';
import { useApi } from '@/hooks/api';
import { useUserState } from '@/hooks/store';
import apiRequest from '@/utils/apiRequest';
import { getUserData } from '@/utils/apiRequest/getUserData';
import { allowsEvaluationDateOverride } from '@/utils/promoOffer/allowsEvaluationDateOverride';

export interface PacksFeedResponse {
	feed: {
		id: string;
		title: string;
		tags: Tags;
		packs: Pack[];
		subtitle?: string;
	};
}

interface PacksFeedReturn {
	isLoading: boolean;
	packs: Packs;
	tags: Tag[];
	title?: string;
	subtitle?: string;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any -- mirrors the untyped `error` SWR already surfaces in useSearch
	error: any;
	hasResponse: boolean;
	notFound: boolean;
}

// Matches every cached entry for this feed and user, whatever evaluation_date it was fetched with.
// Pass it to SWR's mutate so invalidation does not depend on the cache key's full shape.
export const matchesPacksFeedKey =
	(packFeedName: string | null | undefined, userId: string | undefined) =>
	(key: unknown): boolean =>
		Array.isArray(key) &&
		key[0] === `packs/feeds/${packFeedName}` &&
		key[1] === packFeedName &&
		key[2] === userId;

export function usePacksFeed(packFeedName?: string | null): PacksFeedReturn {
	const trackedApiRequest = useApi();
	const user = useUserState();
	const searchParams = useSearchParams();
	const evaluationDate = allowsEvaluationDateOverride()
		? (searchParams?.get('evaluation_date') ?? null)
		: null;

	const { data, isValidating, error } = useSWR<PacksFeedResponse>(
		[packFeedName ? `packs/feeds/${packFeedName}` : null, packFeedName, user?.id, evaluationDate],
		async ([feedEndpoint, , , feedEvaluationDate]: [string, string, string | undefined, string | null]) => {
			const args = {
				method: 'GET' as const,
				customHeaders: {
					'x-device-capabilities': 'v1;signedCookie;streamOnly',
				},
			};
			if (feedEvaluationDate) {
				// Bypasses useApi, which logs the full endpoint in a PX analytics event.
				// The QA date override must never reach analytics.
				const res = await apiRequest<PacksFeedResponse>({
					...args,
					endpoint: `${feedEndpoint}?evaluation_date=${encodeURIComponent(feedEvaluationDate)}`,
					userData: getUserData(),
				});
				return res.data;
			}
			const res = await trackedApiRequest<PacksFeedResponse>({ ...args, endpoint: feedEndpoint });
			return res.data;
		},
		{ revalidateOnFocus: false, errorRetryCount: 0 },
	);

	return {
		isLoading: isValidating,
		packs: data?.feed?.packs ?? [],
		tags: data?.feed?.tags ?? [],
		title: data?.feed?.title,
		subtitle: data?.feed?.subtitle,
		error,
		// Positive confirmation that a *usable* payload came back, which `error` alone does
		// not give: the shared axios interceptor rejects on an HTTP status >= 400 and on a
		// network-level failure (DNS, timeout, reset — no response at all), but a malformed
		// 200 (body missing `feed`) resolves with no error and nothing usable either.
		// Checking `data.feed` rather than just `data` means that case also counts as no
		// response, not as a confirmed-empty one — otherwise a caller would 404 a feed
		// that's really just behind a broken payload.
		hasResponse: data?.feed !== undefined,
		// A 404 is the server actively confirming this feed doesn't exist — the one error
		// shape that should override the "don't trust it" caution above and redirect anyway.
		// Every other error (5xx, or a network-level failure with no status at all) stays
		// unconfirmed, since neither means the feed itself doesn't exist.
		notFound: error?.status === 404,
	};
}
